import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Allergen, Cart, DietaryConcern, Menu, SavedOrder, SavedOrderLine } from '../../shared/menu';
import { DISH_LOW_CONFIDENCE_THRESHOLD, findDish } from '../../shared/menu';
import { formatPrice } from '../../shared/currencies';
import { loadMenuLocal } from '../lib/persist';
import { newOrderId, resolveMenuName, useCart, useMenuNames, useOrderHistory, useSettings } from '../lib/store';
import { acquireScreenWake, releaseScreenWake } from '../lib/brightness';
import {
  allergyBannerFor,
  confirmWithServerFor,
  orderGreetingFor,
  orderThanksFor,
  overrideWarnFor,
  useT,
} from '../lib/i18n';
import { computeOverrideAllergens, religiousCombinedFor } from '../lib/dietaryPhrasebook';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DietaryQuestionChip } from '../components/DietaryQuestionChip';
import { fetchMe, saveMenu, syncOrder } from '../lib/api';

interface DisplayLine {
  dishId: string;
  nameOriginal: string;
  nameTranslated: string;
  quantity: number;
  modifications: string;
  dietaryTags: DietaryConcern[];
  priceText: string | null;
  price: number | null;
  confidence: number;
  overridden: boolean;
  allergens: Allergen[];
}

export default function OrderScreen() {
  const { menuId, orderId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const cart = useCart((s) => s.cart);
  const clearCart = useCart((s) => s.clear);
  const allergens = useSettings((s) => s.hardFilterAllergens);
  const orders = useOrderHistory((s) => s.orders);
  const addOrder = useOrderHistory((s) => s.add);
  const markSynced = useOrderHistory((s) => s.markSynced);
  const namesMap = useMenuNames((s) => s.names);

  const isHistory = Boolean(orderId);
  const historyOrder = isHistory ? orders.find((o) => o.id === orderId) ?? null : null;

  const [menu, setMenu] = useState<Menu | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // For history mode we read the source language straight off the saved snapshot.
  // For live mode we need the live menu doc to pull source language + currency.
  const effectiveMenuId = isHistory ? historyOrder?.menuId ?? null : menuId ?? null;

  useEffect(() => {
    if (!effectiveMenuId) return;
    let cancelled = false;
    loadMenuLocal(effectiveMenuId).then((stored) => {
      if (!cancelled && stored) setMenu(stored.menu);
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveMenuId]);

  useEffect(() => {
    document.body.style.background = '#FFFFFF';
    void acquireScreenWake();
    return () => {
      document.body.style.background = '';
      void releaseScreenWake();
    };
  }, []);

  const lines = useMemo<DisplayLine[]>(() => {
    if (isHistory) {
      if (!historyOrder) return [];
      return historyOrder.lines.map((l) => ({ ...l }));
    }
    if (!menu || !cart) return [];
    const out: DisplayLine[] = [];
    for (const l of cart.lines) {
      const found = findDish(menu, l.dishId);
      if (!found) continue;
      out.push({
        dishId: l.dishId,
        nameOriginal: found.dish.nameOriginal,
        nameTranslated: found.dish.nameTranslated,
        quantity: l.quantity,
        modifications: l.modifications,
        dietaryTags: l.dietaryTags ?? [],
        priceText: found.dish.priceText,
        price: found.dish.price,
        confidence: found.dish.confidence,
        overridden: l.overridden ?? false,
        allergens: found.dish.allergens,
      });
    }
    return out;
  }, [cart, menu, historyOrder, isHistory]);

  const total = useMemo(() => {
    if (isHistory) return historyOrder?.total ?? 0;
    return lines.reduce((acc, x) => acc + (x.price ?? 0) * x.quantity, 0);
  }, [lines, historyOrder, isHistory]);

  const sourceLang = isHistory
    ? historyOrder?.sourceLanguage ?? 'en'
    : menu?.detectedSourceLanguage ?? 'en';
  const currency = isHistory ? historyOrder?.currency ?? null : menu?.currency ?? null;

  const handleConfirmDone = async () => {
    if (submitting) return;
    setSubmitting(true);
    if (!menu || !cart || !menuId) {
      setSubmitting(false);
      setConfirmOpen(false);
      return;
    }

    const order: SavedOrder = {
      id: newOrderId(),
      menuId,
      restaurantName: resolveMenuName(menuId, menu.restaurantName, namesMap),
      sourceLanguage: menu.detectedSourceLanguage,
      targetLanguage: menu.targetLanguage,
      currency: menu.currency ?? null,
      total,
      lines: lines.map<SavedOrderLine>((l) => ({
        dishId: l.dishId,
        nameOriginal: l.nameOriginal,
        nameTranslated: l.nameTranslated,
        quantity: l.quantity,
        modifications: l.modifications,
        dietaryTags: l.dietaryTags,
        priceText: l.priceText,
        price: l.price,
        overridden: l.overridden,
        allergens: l.allergens,
        confidence: l.confidence,
      })),
      finalizedAt: new Date().toISOString(),
      syncedAt: null,
    };

    addOrder(order);
    const snapshotCart: Cart = { menuId, lines: [...cart.lines] };
    clearCart();

    // Best-effort sync. If the user is not signed in (401) or offline, leave
    // the order unsynced — it'll be retried on next app load.
    try {
      const me = await fetchMe();
      if (me.user) {
        const r = await syncOrder(order);
        markSynced(order.id, r.syncedAt);
        // Pin the menu to Saved when the user places an order against it —
        // they'll want to revisit. Idempotent server-side; ignore failures.
        void saveMenu(menuId).catch(() => {});
      }
    } catch {
      // Network or auth issue — order stays in local store, syncedAt = null.
    }

    setConfirmOpen(false);
    setSubmitting(false);
    navigate(`/menu/${menuId}`, {
      state: { undoOrderId: order.id, undoCart: snapshotCart },
    });
  };

  const handleModify = () => {
    if (!menuId) return;
    navigate(`/menu/${menuId}`);
  };

  if (!effectiveMenuId) {
    return (
      <div className="min-h-dvh container-app safe-top safe-bottom flex flex-col items-center justify-center gap-6 text-center">
        <p>Order not found.</p>
        <button onClick={() => navigate('/me')} className="btn-primary">Back</button>
      </div>
    );
  }

  if (isHistory && !historyOrder) {
    return (
      <div className="min-h-dvh container-app safe-top safe-bottom flex flex-col items-center justify-center gap-6 text-center">
        <p>Order not found.</p>
        <button onClick={() => navigate('/me')} className="btn-primary">Back</button>
      </div>
    );
  }

  if (!isHistory && !menu) {
    return (
      <div className="min-h-dvh container-app flex items-center justify-center">
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="min-h-dvh container-app safe-top safe-bottom flex flex-col items-center justify-center gap-6 text-center">
        <p>{isHistory ? 'This order has no items.' : 'Your order is empty.'}</p>
        {!isHistory && effectiveMenuId ? (
          <button
            onClick={() => navigate(`/menu/${effectiveMenuId}`)}
            className="btn-primary"
          >
            ← Back to menu
          </button>
        ) : (
          <button onClick={() => navigate(-1)} className="btn-primary">Back</button>
        )}
      </div>
    );
  }

  const greeting = orderGreetingFor(sourceLang);
  const thanks = orderThanksFor(sourceLang);
  const allergyLabel = allergyBannerFor(sourceLang);
  const confirmServer = confirmWithServerFor(sourceLang);

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: '#FFFFFF',
        color: '#0A0A0A',
        padding: 'max(28px, env(safe-area-inset-top)) 24px max(28px, env(safe-area-inset-bottom))',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <p
        lang={sourceLang}
        style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.25, margin: 0, marginBottom: 18, letterSpacing: '-0.01em' }}
      >
        {greeting}
      </p>

      {allergens.length > 0 && (
        <div
          style={{
            background: '#C4332B',
            color: '#FFFFFF',
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 12,
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: '0.02em',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div>
            <div style={{ fontSize: 14, opacity: 0.9, fontWeight: 600 }}>{allergyLabel}</div>
            <div lang={sourceLang} style={{ fontSize: 18, lineHeight: 1.3 }}>{allergens.join(', ')}</div>
          </div>
        </div>
      )}

      <p
        lang={confirmServer.lang}
        style={{
          fontSize: 12,
          color: '#666',
          margin: '0 0 18px',
          fontStyle: 'italic',
          textAlign: 'center',
        }}
      >
        {confirmServer.text}
      </p>

      <ol style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
        {lines.map((x, idx) => (
          <li
            key={x.dishId}
            style={{
              display: 'flex',
              gap: 16,
              padding: '16px 0',
              borderBottom: idx < lines.length - 1 ? '1px solid #E5E5E5' : 'none',
            }}
          >
            <div
              className="num"
              style={{
                fontSize: 26,
                fontWeight: 700,
                minWidth: 48,
                textAlign: 'right',
                color: '#0A0A0A',
                lineHeight: 1.05,
              }}
            >
              {x.quantity}×
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div lang={sourceLang} style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.25, display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <span>{x.nameOriginal}</span>
                {x.confidence < DISH_LOW_CONFIDENCE_THRESHOLD && (
                  <span
                    aria-label="Uncertain translation"
                    title="Translation uncertain"
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#666',
                      border: '1px dashed #CCC',
                      borderRadius: 9999,
                      padding: '1px 7px',
                      letterSpacing: '0.06em',
                    }}
                  >
                    ?
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                {x.nameTranslated}
              </div>
              {x.overridden && (() => {
                const conflicting = computeOverrideAllergens(x.dietaryTags, x.allergens);
                if (conflicting.length === 0) return null;
                const warn = overrideWarnFor(sourceLang, conflicting);
                return (
                  <div
                    role="alert"
                    style={{
                      marginTop: 8,
                      padding: '10px 12px',
                      background: '#C4332B',
                      color: '#FFFFFF',
                      borderRadius: 6,
                      lineHeight: 1.35,
                    }}
                  >
                    <div lang={warn.lang} style={{ fontSize: 14, fontWeight: 700 }}>
                      {warn.text}
                    </div>
                    {!warn.translated && (
                      <div style={{ marginTop: 4, fontSize: 11, fontWeight: 500, opacity: 0.9, fontStyle: 'italic' }}>
                        Shown in English — translation coming soon.
                      </div>
                    )}
                  </div>
                );
              })()}
              {x.modifications && (
                <div lang={sourceLang} style={{ fontSize: 14, color: '#0A0A0A', marginTop: 6, fontStyle: 'italic' }}>
                  → {x.modifications}
                </div>
              )}
              {x.dietaryTags.length > 0 && (() => {
                // Consolidate halal/kosher strict tags into a single "I keep
                // halal/kosher" card so the waiter reads one coherent ask
                // instead of three red chips. Tags outside the religious
                // group (e.g. no_fish_sauce, no_dairy without religious
                // reason) still render as individual chips below.
                const strict = x.dietaryTags.filter((c) => c.severity === 'strict');
                const halalStrict = strict.filter((c) => c.reason === 'halal');
                const kosherStrict = strict.filter((c) => c.reason === 'kosher');
                let consolidatedReason: 'halal' | 'kosher' | null = null;
                let absorbedTags = new Set<string>();
                if (halalStrict.length >= 2) {
                  consolidatedReason = 'halal';
                  halalStrict.forEach((c) => absorbedTags.add(c.tag));
                } else if (kosherStrict.length >= 2) {
                  consolidatedReason = 'kosher';
                  kosherStrict.forEach((c) => absorbedTags.add(c.tag));
                }
                const remainingTags = x.dietaryTags.filter((c) => !absorbedTags.has(c.tag));
                const combined = consolidatedReason ? religiousCombinedFor(consolidatedReason, sourceLang) : null;
                return (
                  <>
                    {combined && (
                      <div
                        role="alert"
                        style={{
                          marginTop: 8,
                          padding: '10px 14px',
                          borderRadius: 10,
                          border: '2px solid #C4332B',
                          background: 'rgba(196,51,43,0.08)',
                          color: '#0A0A0A',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, color: '#C4332B' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                          {consolidatedReason === 'halal' ? 'Halal' : 'Kosher'}
                        </div>
                        <p lang={combined.lang} style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.4, fontWeight: 500 }}>
                          {combined.text}
                        </p>
                        {!combined.translated && (
                          <p style={{ margin: '6px 0 0', fontSize: 11, fontStyle: 'italic', color: '#7A2018' }}>
                            Shown in English — translation coming soon.
                          </p>
                        )}
                      </div>
                    )}
                    {remainingTags.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: combined ? 6 : 4 }}>
                        {remainingTags.map((c) => (
                          <DietaryQuestionChip
                            key={`${c.tag}-${c.severity}`}
                            concern={c}
                            sourceLanguage={sourceLang}
                          />
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            {x.priceText && (
              <div className="num" style={{ fontSize: 14, color: '#666', alignSelf: 'flex-start' }}>
                {x.priceText}
              </div>
            )}
          </li>
        ))}
      </ol>

      {total > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            paddingTop: 16,
            marginTop: 12,
            borderTop: '2px solid #0A0A0A',
            fontSize: 22,
            fontWeight: 600,
          }}
        >
          <span lang={sourceLang}>Total</span>
          <span className="num">{formatPrice(total, currency)}</span>
        </div>
      )}

      <p lang={sourceLang} style={{ fontSize: 18, marginTop: 24, marginBottom: 0, color: '#0A0A0A' }}>
        {thanks}
      </p>

      {!isHistory && (
        <div style={{ display: 'flex', gap: 12, marginTop: 24, alignItems: 'stretch' }}>
          <button
            onClick={handleModify}
            style={{
              flex: 0.9,
              padding: '14px',
              borderRadius: 12,
              border: '1px solid #DDD',
              background: 'transparent',
              color: '#0A0A0A',
              fontSize: 15,
              fontWeight: 500,
              minHeight: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('order.modify')}
          </button>
          <button
            onClick={() => setConfirmOpen(true)}
            className="btn-primary"
            style={{
              flex: 1.4,
              minHeight: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontSize: 16,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {t('order.placed')}
          </button>
        </div>
      )}

      {isHistory && (
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            onClick={() => navigate('/me')}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: 12,
              border: '1px solid #DDD',
              background: 'transparent',
              color: '#0A0A0A',
              fontSize: 15,
              fontWeight: 500,
              minHeight: 48,
            }}
          >
            ← Back
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={t('confirm.title')}
        body={t('confirm.body')}
        confirmLabel={t('confirm.yes')}
        cancelLabel={t('confirm.cancel')}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDone}
      />
    </div>
  );
}

